import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ChapterLabel, Reveal, ArrowButton } from "@/components/landing/Reveal";
import { waLink, WA_MESSAGES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full rounded-sm border border-white/15 bg-[#0A0A0A] px-4 py-3.5 text-sm text-ivory placeholder:text-muted/60 transition-colors duration-300 focus:border-gold focus:outline-none";

const BookingSection = () => {
    const [form, setForm] = useState({
        name: "",
        destination: "Bangkok",
        date: "",
        guests: 2,
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/leads`, {
                ...form,
                guests: Number(form.guests),
            });
            setSubmitted(true);
            toast.success("Request received — our concierge will be in touch.");
        } catch {
            toast.error("Something went wrong. Please reach us on WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="book"
            data-testid="booking-section"
            className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36"
        >
            <div className="grid gap-16 md:grid-cols-2 md:gap-20">
                <div>
                    <Reveal>
                        <ChapterLabel number="Nº 07" title="Reservations" testId="booking-label" />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-8 font-serif text-3xl font-medium leading-tight tracking-tight text-ivory md:text-5xl">
                            YOUR NIGHT STARTS{" "}
                            <span className="italic text-gold">BEFORE YOU ARRIVE.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-8 max-w-md text-sm leading-relaxed text-muted md:text-base">
                            Tell us where you're going, when you're going and
                            how many people are coming. We'll help you find the
                            right VIP experience.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                            <ArrowButton
                                testId="booking-book-vip-btn"
                                href={waLink(WA_MESSAGES.booking)}
                            >
                                Book My VIP Table
                            </ArrowButton>
                            <ArrowButton
                                testId="booking-concierge-btn"
                                variant="secondary"
                                href={waLink(WA_MESSAGES.concierge)}
                            >
                                WhatsApp Concierge
                            </ArrowButton>
                        </div>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <Link
                            data-testid="booking-quiz-link"
                            to="/plan"
                            className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold"
                        >
                            Not sure yet? Build your night in 60 seconds →
                        </Link>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <div className="rounded-sm border border-white/10 bg-[#0F0F0F] p-8 md:p-10">
                        {submitted ? (
                            <div
                                data-testid="lead-form-success"
                                className="flex h-full min-h-[320px] flex-col items-start justify-center gap-6"
                            >
                                <span className="font-serif text-3xl italic text-gold">
                                    Request received.
                                </span>
                                <p className="text-sm leading-relaxed text-muted">
                                    Our concierge has your details. For the
                                    fastest response, continue the conversation
                                    on WhatsApp now.
                                </p>
                                <ArrowButton
                                    testId="lead-whatsapp-continue-btn"
                                    href={waLink(
                                        `Hi BookMyVIP, I'm ${form.name}. I'd like a VIP table in ${form.destination} on ${form.date} for ${form.guests} guests.`
                                    )}
                                >
                                    Continue on WhatsApp
                                </ArrowButton>
                            </div>
                        ) : (
                            <form
                                data-testid="lead-form"
                                onSubmit={submit}
                                className="flex flex-col gap-5"
                            >
                                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
                                    Request a callback
                                </p>
                                <div>
                                    <label htmlFor="lead-name" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                        Name
                                    </label>
                                    <input
                                        id="lead-name"
                                        data-testid="lead-name-input"
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        className={inputCls}
                                    />
                                </div>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="lead-destination" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                            Destination
                                        </label>
                                        <select
                                            id="lead-destination"
                                            data-testid="lead-destination-select"
                                            value={form.destination}
                                            onChange={set("destination")}
                                            className={inputCls}
                                        >
                                            {["Bangkok", "Phuket", "Pattaya", "Koh Samui", "Other"].map((d) => (
                                                <option key={d} value={d}>
                                                    {d}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="lead-guests" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                            Guests
                                        </label>
                                        <input
                                            id="lead-guests"
                                            data-testid="lead-guests-input"
                                            type="number"
                                            min="1"
                                            max="200"
                                            required
                                            value={form.guests}
                                            onChange={set("guests")}
                                            className={inputCls}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="lead-date" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
                                        Date
                                    </label>
                                    <input
                                        id="lead-date"
                                        data-testid="lead-date-input"
                                        type="date"
                                        required
                                        value={form.date}
                                        onChange={set("date")}
                                        className={`${inputCls} [color-scheme:dark]`}
                                    />
                                </div>
                                <button
                                    data-testid="lead-submit-button"
                                    type="submit"
                                    disabled={loading}
                                    className="mt-2 inline-flex items-center justify-center gap-3 rounded-sm bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-gold-soft disabled:opacity-60"
                                >
                                    {loading && (
                                        <Loader2 size={14} className="animate-spin" />
                                    )}
                                    {loading ? "Sending…" : "Request My VIP Table"}
                                </button>
                                <p className="text-[11px] leading-relaxed text-muted/70">
                                    No payment required — our concierge will
                                    confirm availability and options with you
                                    directly.
                                </p>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default BookingSection;
